import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { cn } from "@/utils";
import { expandCollapse, durations, getVariants } from "@/lib/animation";

interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  className?: string;
}

export function CollapsibleSection({
  title,
  icon,
  count,
  children,
  defaultOpen = true,
  storageKey,
  className,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return defaultOpen;
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? stored === "true" : defaultOpen;
    }
    return defaultOpen;
  });

  const shouldReduceMotion = useReducedMotion();
  const variants = getVariants(expandCollapse, shouldReduceMotion);

  useEffect(() => {
    if (storageKey && typeof window !== "undefined") {
      localStorage.setItem(storageKey, String(isOpen));
    }
  }, [isOpen, storageKey]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={cn("space-y-3", className)}>
      <button
        onClick={handleToggle}
        className="flex items-center justify-between w-full group text-left"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <h2 className="text-base md:text-lg font-semibold">{title}</h2>
          {count !== undefined && (
            <span className="text-xs md:text-sm px-1.5 md:px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
              {count}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 0 : -90 }}
          transition={{ duration: shouldReduceMotion ? 0 : durations.normal }}
          className="text-muted-foreground group-hover:text-foreground transition-colors"
        >
          <ChevronDown className="h-4 w-4 md:h-5 md:w-5" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            variants={variants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
